/**
 * DemoGate — login wall for the HustlePlaza Demo-Day playbook.
 *
 * Email + password is POSTed to /api/directory/plaza/demo-login. The
 * credentials live in backend/.env so they never ship in the JS bundle.
 *
 * Unlock is cached in localStorage so investors only sign in once per
 * browser. ?key=<password>&user=<email> also works as a shareable bypass.
 */
import { useEffect, useState } from "react";
import { Lock, ArrowRight } from "lucide-react";

const DEMO_UNLOCK_KEY = "hpz-demo-unlocked";
const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
async function verify(email, password) {
  const res = await fetch(`${API}/api/directory/plaza/demo-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (res.ok) return true;
  const data = await res.json().catch(() => ({}));
  throw new Error(data?.detail || "Wrong email or password.");
}

export default function DemoGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      if (localStorage.getItem(DEMO_UNLOCK_KEY) === "1") return true;
    } catch { /* ignore */ }
    return false;
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // ?key=… and ?user=… shareable-link bypass on first paint.
  useEffect(() => {
    if (unlocked) return;
    try {
      const url = new URL(window.location.href);
      const key = url.searchParams.get("key");
      const user = url.searchParams.get("user");
      if (key && user) {
        verify(user, key).then((ok) => {
          if (ok) {
            try { localStorage.setItem(DEMO_UNLOCK_KEY, "1"); } catch { /* ignore */ }
            url.searchParams.delete("key");
            url.searchParams.delete("user");
            window.history.replaceState({}, "", url.toString());
            setUnlocked(true);
          }
        }).catch(() => { /* silent — fall through to manual form */ });
      }
    } catch { /* ignore */ }
  }, [unlocked]);

  if (unlocked) return children;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setErr("Email and password are required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await verify(email.trim(), password);
      try { localStorage.setItem(DEMO_UNLOCK_KEY, "1"); } catch { /* ignore */ }
      setUnlocked(true);
    } catch (e2) {
      setErr(e2.message || "Wrong email or password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.wrap} data-testid="demo-gate">
      <div style={styles.card}>
        <div style={styles.iconBox}>
          <Lock size={22} strokeWidth={1.8} />
        </div>
        <p style={styles.eyebrow}>INTERNAL · PLAYBOOK</p>
        <h1 style={styles.title}>HustlePlaza Demo Day</h1>
        <p style={styles.sub}>
          This walkthrough is shared with founders, investors, and the
          founding-vendor cohort. Sign in with the credentials we sent
          to continue.
        </p>
        <form onSubmit={submit} style={styles.form}>
          <input
            type="email"
            autoFocus
            placeholder="Email"
            autoComplete="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErr(""); }}
            style={styles.input}
            data-testid="demo-gate-email"
          />
          <input
            type="password"
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setErr(""); }}
            style={styles.input}
            data-testid="demo-gate-password"
          />
          <button
            type="submit"
            style={{ ...styles.btn, opacity: busy ? 0.6 : 1 }}
            disabled={busy}
            data-testid="demo-gate-submit"
          >
            {busy ? "Signing in…" : <>Enter <ArrowRight size={14} /></>}
          </button>
        </form>
        {err && <p style={styles.err} data-testid="demo-gate-err">{err}</p>}
        <p style={styles.foot}>
          Don't have an account?{" "}
          <a href="mailto:hello@hustleplate.au" style={styles.link}>
            Request access
          </a>
          .
        </p>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: "100vh",
    background: "radial-gradient(circle at 50% 0%, #2a1f08 0%, #0d0a05 60%)",
    color: "#F3E9CE",
    display: "grid",
    placeItems: "center",
    padding: "24px",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "rgba(20, 16, 8, 0.85)",
    border: "1px solid rgba(243,200,75,0.18)",
    borderRadius: 18,
    padding: "40px 32px 32px",
    backdropFilter: "blur(16px)",
    boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
  },
  iconBox: {
    width: 44, height: 44, borderRadius: 12,
    background: "rgba(243,200,75,0.12)",
    color: "#F3C84B",
    display: "grid", placeItems: "center",
    marginBottom: 18,
  },
  eyebrow: { fontSize: 11, letterSpacing: 2, color: "#F3C84B", margin: 0 },
  title: { fontSize: 28, margin: "8px 0 12px", fontWeight: 600, letterSpacing: -0.5 },
  sub: { fontSize: 14, lineHeight: 1.55, color: "#B7AC8B", margin: "0 0 22px" },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  input: {
    padding: "12px 14px", fontSize: 14,
    background: "#0f0c06", border: "1px solid rgba(243,200,75,0.25)",
    borderRadius: 10, color: "#F3E9CE", outline: "none",
  },
  btn: {
    padding: "12px 16px", fontSize: 14, fontWeight: 600,
    background: "#F3C84B", color: "#1a1408",
    border: "none", borderRadius: 10, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  },
  err: { color: "#FF9F87", fontSize: 13, margin: "10px 0 0" },
  foot: { fontSize: 12, color: "#80785f", margin: "20px 0 0" },
  link: { color: "#F3C84B", textDecoration: "none" },
};
